import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Ship, Menu, X, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/rates?shipment_type=export", label: "Export Rates" },
  { to: "/rates?shipment_type=import", label: "Import Rates" },
  { to: "/shipping-lines", label: "Shipping Lines" },
  { to: "/local-charges", label: "Local Charges" },
  { to: "/calculator", label: "Calculator" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-navy text-white border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <div className="h-9 w-9 bg-cyan flex items-center justify-center rounded-sm">
            <Ship className="h-5 w-5 text-navy" strokeWidth={2.2} />
          </div>
          <div className="leading-none">
            <div className="font-heading font-extrabold text-lg tracking-tight">VK Forwarders</div>
            <div className="text-[10px] text-white/50 tracking-wide uppercase">Sea Freight Platform</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="px-3 py-2 text-sm text-white/75 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              {user.role === "admin" && (
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/10"
                  onClick={() => navigate("/admin")} data-testid="nav-admin-btn">
                  <ShieldCheck className="h-4 w-4 mr-1.5" /> Admin
                </Button>
              )}
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10"
                onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn">
                <LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard
              </Button>
              <Button variant="ghost" size="sm" className="text-white/70 hover:bg-white/10"
                onClick={logout} data-testid="nav-logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button size="sm" className="bg-signal hover:bg-signal/90 text-white rounded-sm"
              onClick={() => navigate("/login")} data-testid="nav-login-btn">
              Login / Register
            </Button>
          )}
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 px-4 py-3 space-y-1 bg-navy">
          {LINKS.map((l) => (
            <Link key={l.label} to={l.to} onClick={() => setOpen(false)}
              className="block px-3 py-2.5 text-sm text-white/80 hover:bg-white/5 rounded-sm">
              {l.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-white/10 flex flex-col gap-2">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Button variant="outline" size="sm" className="border-white/20 text-white bg-transparent"
                    onClick={() => { setOpen(false); navigate("/admin"); }}>Admin</Button>
                )}
                <Button variant="outline" size="sm" className="border-white/20 text-white bg-transparent"
                  onClick={() => { setOpen(false); navigate("/dashboard"); }}>Dashboard</Button>
                <Button variant="ghost" size="sm" className="text-white/70" onClick={logout}>Logout</Button>
              </>
            ) : (
              <Button size="sm" className="bg-signal text-white" onClick={() => { setOpen(false); navigate("/login"); }}>
                Login / Register
              </Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
